/*
 * kp-sms-capture.js — the SMS capture call to action and its popup.
 *
 * <kp-sms-capture> runs one of two modes (snippets/kp-sms-capture.liquid):
 *
 *   text  The visitor texts the keyword. A tap on the sms: link is counted,
 *         remembered, and swapped for the "now hit send" panel. With a mouse
 *         there is nothing to tap, so a QR code of the same link is drawn —
 *         the QR library loads only then, and only once.
 *   form  One US number goes to the Worker (workers/sms-subscribe) with an
 *         invisible Turnstile token, and the form is swapped for the
 *         "reply Y" panel.
 *
 * Neither mode ever shows a code: Postscript texts it.
 *
 * <kp-sms-popup> decides when the popup's <dialog> opens by itself. Desktop:
 * after the delay or on exit intent. A phone (below 750px): never (owner,
 * 2026-09-24). Other touch screens: past a share of the page. It opens by
 * itself once; once closed (or once someone has signed up from any surface)
 * it stays shut and a tab on the left edge brings it back on demand. Never
 * on its own in the theme editor, never over a drawer.
 *
 * While the price ladder is on, the popup is the ladder's (data-ladder,
 * snippets/kp-sms-popup.liquid): it opens by itself on exit intent only, and
 * its button attaches the store code through window.kpApplyCode
 * (kp-copy-code.js) and shows what the cart answered. Once the code is taken
 * — on the cart when the page was built, or anywhere on this page since —
 * it no longer opens by itself.
 *
 * Events on Shopify's customer-events bus, for the web pixels:
 * `sms_capture_view` (popup opened, or an inline surface half in view),
 * `sms_capture_click` (text mode: the sms: link was tapped, the form's or a
 * texting strip's) and `sms_capture_submit` (form mode: the Worker accepted
 * a number).
 *
 * Nothing here touches the cart (hard rule 2): the ladder's button asks
 * kpApplyCode, which does, and only shows its answer.
 *
 * Wrapped in an IIFE and guarded: the snippet can render more than once on a
 * page, and Dawn's product swap re-runs scripts inside <main> (invariant 6).
 */
(() => {
  if (customElements.get('kp-sms-capture')) return;

  const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  const TURNSTILE_ACTION = 'sms_capture';
  const TOKEN_TIMEOUT_MS = 20000;
  const SENT_PREFIX = 'kp-sms:sent:';
  const SNOOZE_PREFIX = 'kp-sms:snooze:';
  const DAY_MS = 864e5;
  // Long enough for the messages app to take over before the panel swaps;
  // swapping first would leave a phone user staring at a page that changed.
  const TAP_SWAP_MS = 900;
  // Dawn's page lock for its drawers and the search modal (kp-lenis.js reads
  // the same class). The popup waits while one is up, and uses it itself.
  const PAGE_LOCK = /(^|\s)overflow-hidden(-\w+)?(\s|$)/;
  const FINE_POINTER = '(hover: hover) and (pointer: fine)';
  // --kp-bp-stack: below it the popup never opens by itself.
  const PHONE = 'screen and (max-width: 749px)';

  // Only ONE popup opens by itself per visit, whoever gets there first: this
  // one or the Pocket Drop game (assets/kp-pocket-drop-launcher.js reads and
  // writes the same key). The other can still be opened from its tab. Without
  // this, closing one popup brought the other up a few seconds later.
  const AUTO_KEY = 'kpAutoPopup';
  // The ladder's exit-intent popup waits this long before it may fire: a
  // cursor that leaves the top of the window in the first seconds is a
  // shopper heading for the address bar, not one about to leave.
  const EXIT_MIN_MS = 15000;
  // How many 4-second retries while another overlay is up, before giving up.
  const RETRY_MAX = 6;
  const autoSeen = () => {
    try {
      return !!sessionStorage.getItem(AUTO_KEY);
    } catch (error) {
      return false;
    }
  };
  const markAuto = (who) => {
    try {
      sessionStorage.setItem(AUTO_KEY, who);
    } catch (error) {
      /* blocked: the popup may ask again on the next page */
    }
  };

  const storage = {
    get(key) {
      try {
        return localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch (error) {
        /* storage blocked — the popup may simply ask again */
      }
    },
  };

  /* Past the campaign's end? A cached page can be served after the moment
     Liquid decided it was live, so the browser asks again. */
  function ended(el) {
    const at = Date.parse(el.dataset.endsAt || '');
    return Number.isFinite(at) && at <= Date.now();
  }

  function track(name, detail) {
    try {
      window.Shopify?.analytics?.publish?.(name, detail);
    } catch (error) {
      /* analytics is never worth a broken form */
    }
  }

  const scripts = new Map();
  function loadScript(src) {
    if (!scripts.has(src)) {
      scripts.set(
        src,
        new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = src;
          script.async = true;
          script.onload = resolve;
          script.onerror = () => {
            scripts.delete(src);
            reject(new Error(`failed to load ${src}`));
          };
          document.head.appendChild(script);
        })
      );
    }
    return scripts.get(src);
  }

  /* Same rule as the Worker's: ten digits, area code and exchange 2–9. */
  function toUsE164(raw) {
    const text = String(raw || '').trim();
    let digits = text.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
    else if (text.startsWith('+')) return '';
    if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(digits)) return '';
    return `+1${digits}`;
  }

  /* One <path> of unit squares over a quiet-zone square; colours are CSS's. */
  function qrSvg(text) {
    const qr = window.qrcode(0, 'M');
    qr.addData(text);
    qr.make();
    const count = qr.getModuleCount();
    const quiet = 2;
    const size = count + quiet * 2;
    let d = '';
    for (let row = 0; row < count; row += 1) {
      for (let col = 0; col < count; col += 1) {
        if (qr.isDark(row, col)) d += `M${col + quiet} ${row + quiet}h1v1h-1z`;
      }
    }
    return `<svg viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges" aria-hidden="true" focusable="false"><rect class="kp-sms__qr-ground" width="${size}" height="${size}"/><path class="kp-sms__qr-modules" d="${d}"/></svg>`;
  }

  class KpSmsCapture extends HTMLElement {
    connectedCallback() {
      if (this.dataset.kpReady) return;
      this.form = this.querySelector('[data-kp-sms-form]');
      this.done = this.querySelector('[data-kp-sms-done]');
      if (!this.form || !this.done) return;
      this.dataset.kpReady = '1';

      if (ended(this)) {
        (this.closest('.kp-pdp-sms, .kp-sms-band') || this).hidden = true;
        return;
      }

      if (this.dataset.mode === 'form') this.setupForm();
      else this.setupText();

      // The popup reports its own view when it opens.
      if (this.dataset.source !== 'popup') this.watchView();
    }

    get detail() {
      return { source: this.dataset.source, keyword: this.dataset.keyword, mode: this.dataset.mode };
    }

    watchView() {
      if (!('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          track('sms_capture_view', this.detail);
        },
        { threshold: 0.5 }
      );
      observer.observe(this);
    }

    /* Signed up (or, in text mode, on the way to): remembered for the popup, announced for the page. */
    finish() {
      storage.set(SENT_PREFIX + this.dataset.keyword, String(Date.now()));
      this.form.hidden = true;
      this.done.hidden = false;
      this.querySelector('[data-kp-sms-done-focus]')?.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('kp-sms:sent', { bubbles: true, detail: this.detail }));
    }

    /* ── Text mode ─────────────────────────────────────────────────────── */

    setupText() {
      this.querySelector('[data-kp-sms-link]')?.addEventListener('click', () => {
        track('sms_capture_click', this.detail);
        setTimeout(() => this.finish(), TAP_SWAP_MS);
      });

      const qr = this.querySelector('[data-kp-sms-qr]');
      if (!qr || !this.dataset.qrSrc || !window.matchMedia(FINE_POINTER).matches) return;
      loadScript(this.dataset.qrSrc)
        .then(() => {
          qr.innerHTML = qrSvg(this.dataset.smsHref);
        })
        .catch(() => {
          /* no QR — the "or text KEYWORD to NUMBER" line under it still works */
        });
    }

    /* ── Form mode ─────────────────────────────────────────────────────── */

    setupForm() {
      this.phone = this.querySelector('[data-kp-sms-phone]');
      this.submit = this.querySelector('[data-kp-sms-submit]');
      this.message = this.querySelector('[data-kp-sms-message]');
      this.challenge = this.querySelector('[data-kp-sms-challenge]');
      if (!this.phone) return;

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.send();
      });
      // The challenge script loads on intent, not on page load.
      this.phone.addEventListener('focus', () => this.widget().catch(() => {}), { once: true });
      this.phone.addEventListener('input', () => {
        if (!this.phone.hasAttribute('aria-invalid')) return;
        this.phone.removeAttribute('aria-invalid');
        this.say('');
      });
    }

    widget() {
      if (!this.widgetReady) {
        this.widgetReady = loadScript(TURNSTILE_SRC)
          .then(() =>
            window.turnstile.render(this.challenge, {
              sitekey: this.dataset.siteKey,
              action: TURNSTILE_ACTION,
              execution: 'execute',
              appearance: 'interaction-only',
              callback: (token) => this.settle?.(token),
              'error-callback': () => {
                this.settle?.('');
                return true;
              },
              'expired-callback': () => {},
            })
          )
          .catch((error) => {
            this.widgetReady = null;
            throw error;
          });
      }
      return this.widgetReady;
    }

    async token() {
      const id = await this.widget();
      // A token is single use; the widget must be reset before a second run.
      if (this.challenged) window.turnstile.reset(id);
      this.challenged = true;
      return new Promise((resolve) => {
        const timer = setTimeout(() => this.settle(''), TOKEN_TIMEOUT_MS);
        this.settle = (token) => {
          clearTimeout(timer);
          this.settle = null;
          resolve(token);
        };
        window.turnstile.execute(this.challenge);
      });
    }

    async send() {
      if (this.sending) return;
      this.say('');

      const website = this.querySelector('[data-kp-sms-hp]')?.value || '';
      const phone = toUsE164(this.phone.value);
      if (!phone && !website) {
        this.phone.setAttribute('aria-invalid', 'true');
        this.phone.focus();
        this.say(this.dataset.msgInvalid, true);
        return;
      }

      this.busy(true);
      try {
        const token = website ? '' : await this.token().catch(() => '');
        const response = await fetch(this.dataset.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone, keyword: this.dataset.keyword, source: this.dataset.source, token, website }),
        });
        const result = await response.json().catch(() => ({}));

        if (response.ok && result.ok) {
          track('sms_capture_submit', this.detail);
          this.finish();
        } else if (result.error === 'invalid_phone') {
          this.phone.setAttribute('aria-invalid', 'true');
          this.say(this.dataset.msgInvalid, true);
        } else if (response.status === 429) {
          this.say(this.dataset.msgLimited, true);
        } else {
          this.say(this.dataset.msgFailed, true);
        }
      } catch (error) {
        this.say(this.dataset.msgFailed, true);
      } finally {
        this.busy(false);
      }
    }

    /* Typed values stay through a failure (Guideline §22): read-only, not cleared. */
    busy(on) {
      this.sending = on;
      this.phone.readOnly = on;
      this.submit.toggleAttribute('disabled', on);
      this.submit.setAttribute('aria-busy', String(on));
      this.submit.querySelector('.kp-sms__submit-label').hidden = on;
      this.submit.querySelector('.kp-loader').hidden = !on;
    }

    say(text, error = false) {
      this.message.textContent = text || '';
      this.message.hidden = !text;
      this.message.classList.toggle('kp-sms__message--error', !!text && error);
    }
  }

  class KpSmsPopup extends HTMLElement {
    connectedCallback() {
      if (this.dataset.kpReady) return;
      this.dialog = this.querySelector('[data-kp-sms-dialog]');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.dataset.kpReady = '1';
      this.keyword = this.dataset.keyword;
      this.ladder = 'ladder' in this.dataset;
      this.taken = 'taken' in this.dataset;
      const designMode = 'designMode' in this.dataset;
      this.tab = this.dataset.tab === 'true' || designMode ? this.querySelector('[data-kp-sms-tab]') : null;
      if (ended(this)) return;

      // The × and, in the ladder, the decline under the offer.
      this.querySelectorAll('[data-kp-sms-close]').forEach((button) => {
        button.addEventListener('click', () => this.dialog.close());
      });
      // A click that lands on the <dialog> itself, not its panel, is the scrim.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => this.closed());
      this.tab?.querySelector('button')?.addEventListener('click', () => this.show());
      if (this.ladder) this.setupLadder();

      // The editor: never open by itself, but offer the tab to preview it.
      if (designMode) return this.showTab(true);
      if (this.dismissed()) this.showTab(true);
      else if (!this.taken) this.arm();
    }

    /* ── The price ladder ──────────────────────────────────────────────── */

    setupLadder() {
      this.code = String(this.dataset.code || '').toUpperCase();
      this.take = this.querySelector('[data-kp-ladder-take]');
      this.status = this.querySelector('[data-kp-ladder-status]');
      this.take?.addEventListener('click', () => this.takeCode());
      // Taken anywhere on this page (a card's chip, the cart's box, this
      // button): nothing left for the popup to open by itself for.
      document.addEventListener('kp:code-applied', (event) => {
        if (String(event.detail?.code || '').toUpperCase() !== this.code) return;
        this.taken = true;
        this.disarm();
      });
    }

    /* The cart's answer, shown on the button and said in the status line.
       kpApplyCode never rejects: true when the cart took the code, false
       when it refused it or could not be reached. */
    async takeCode() {
      if (!this.take || this.take.dataset.state !== 'idle') return;
      this.setTake('busy');
      this.say('');
      const applied = typeof window.kpApplyCode === 'function' ? await window.kpApplyCode(this.code) : false;
      this.setTake(applied ? 'applied' : 'idle');
      // Applied, the button already reads it, so the line is for screen
      // readers only; refused, it is the only place the answer shows.
      this.say(applied ? this.dataset.msgApplied : this.dataset.msgRefused, { quiet: applied });
    }

    setTake(state) {
      this.take.dataset.state = state;
      this.take.setAttribute('aria-busy', String(state === 'busy'));
    }

    say(text, { quiet = false } = {}) {
      if (!this.status) return;
      this.status.textContent = text || '';
      this.status.hidden = !text;
      this.status.classList.toggle('visually-hidden', quiet);
    }

    disconnectedCallback() {
      this.disarm();
    }

    /* Closed within the snooze window, or already signed up from anywhere. */
    dismissed() {
      if (storage.get(SENT_PREFIX + this.keyword)) return true;
      return Number(storage.get(SNOOZE_PREFIX + this.keyword) || 0) > Date.now();
    }

    showTab(on) {
      if (this.tab) this.tab.hidden = !on;
    }

    arm() {
      // A phone: never by itself. The ladder: exit intent only, so no timer
      // and, on a touch screen with nothing to leave by, nothing at all.
      if (window.matchMedia(PHONE).matches) return;
      const open = () => this.open();
      if (window.matchMedia(FINE_POINTER).matches) {
        const delay = this.ladder ? 0 : Number(this.dataset.delay || 0) * 1000;
        if (delay > 0) this.timer = setTimeout(open, delay);
        if (this.ladder || this.dataset.exit === 'true') {
          // Leaving through the top edge is heading for the tabs or the address bar.
          this.onLeave = (event) => {
            if (!event.relatedTarget && event.clientY <= 0 && performance.now() >= EXIT_MIN_MS) open();
          };
          document.addEventListener('mouseout', this.onLeave);
        }
      } else if (!this.ladder) {
        const share = Number(this.dataset.scroll || 0) / 100;
        this.onScroll = () => {
          const room = document.documentElement.scrollHeight - window.innerHeight;
          if (room <= 0 || window.scrollY / room >= share) open();
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }
    }

    disarm() {
      clearTimeout(this.timer);
      clearTimeout(this.retry);
      if (this.onLeave) document.removeEventListener('mouseout', this.onLeave);
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      this.onLeave = this.onScroll = null;
    }

    /* Opening by itself: politely, once. The window may have narrowed to a
       phone's width since it was armed. */
    open() {
      if (this.dialog.open) return;
      if (this.dismissed() || this.taken || window.matchMedia(PHONE).matches || autoSeen()) return this.disarm();
      // The cart drawer, the menu or search is up: ask again shortly rather
      // than stacking a second overlay on the one the visitor is using, and
      // give up after a few tries instead of waiting for the moment it closes.
      if (PAGE_LOCK.test(document.body.className) || document.querySelector('dialog[open]')) {
        this.tries = (this.tries || 0) + 1;
        if (this.tries > RETRY_MAX) return this.disarm();
        clearTimeout(this.retry);
        this.retry = setTimeout(() => this.open(), 4000);
        return;
      }
      markAuto('sms');
      this.show();
    }

    /* Opening on request (the tab) skips every "should it?" check. */
    show() {
      if (this.dialog.open) return;
      this.disarm();
      this.showTab(false);
      this.dialog.showModal();
      document.body.classList.add('overflow-hidden');
      track('sms_capture_view', { source: 'popup', keyword: this.keyword });
    }

    closed() {
      document.body.classList.remove('overflow-hidden');
      if (!storage.get(SENT_PREFIX + this.keyword)) {
        const days = Number(this.dataset.snoozeDays || 0);
        storage.set(SNOOZE_PREFIX + this.keyword, String(Date.now() + days * DAY_MS));
      }
      // The ladder's answer belonged to that visit. Opened again from the
      // tab, the button offers the code again rather than vouch for a cart
      // that may have changed since; taking it twice changes nothing.
      if (this.take && this.take.dataset.state !== 'busy') {
        this.setTake('idle');
        this.say('');
      }
      this.showTab(true);
    }
  }

  /*
   * The texting strip (snippets/kp-sms-strip.liquid) in the product page's
   * ladder, the cart's box and the ladder popup: a tap on its sms: link is
   * counted like the form's, and remembered the same way, so the popup does
   * not ask again someone already on their way to texting.
   */
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('[data-kp-sms-strip-link]');
    const strip = link?.closest('[data-kp-sms-strip]');
    if (!strip) return;
    track('sms_capture_click', { source: strip.dataset.source || 'strip', keyword: strip.dataset.keyword, mode: 'text' });
    if (strip.dataset.keyword) storage.set(SENT_PREFIX + strip.dataset.keyword, String(Date.now()));
  });

  /*
   * The product card's "Text CUE30" row is an sms: link. On a phone that is
   * the whole job. With a mouse an sms: link mostly opens nothing, so the
   * click opens the popup (it has the QR), or goes to the cue when this page
   * has no popup — the cue's own page carries the campaign's form.
   */
  document.addEventListener('click', (event) => {
    const row = event.target.closest?.('[data-kp-sms-card]');
    if (!row) return;
    track('sms_capture_click', { source: 'card', keyword: row.dataset.keyword, mode: 'text' });
    if (!window.matchMedia(FINE_POINTER).matches) return;
    event.preventDefault();
    const popup = document.querySelector('kp-sms-popup');
    if (popup && typeof popup.show === 'function' && popup.dialog) popup.show();
    else if (row.dataset.fallback) window.location.href = row.dataset.fallback;
  });

  customElements.define('kp-sms-capture', KpSmsCapture);
  customElements.define('kp-sms-popup', KpSmsPopup);
})();
